import axios from 'axios';

export interface CepAddressDto {
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
  complement?: string;
}

interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

/**
 * CEP Service - Calls ViaCEP API directly
 */
export const cepService = {
  /**
   * Lookup address by CEP using ViaCEP API
   */
  getAddressByCep: async (cep: string): Promise<CepAddressDto> => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      throw new Error('CEP deve ter 8 dígitos');
    }

    try {
      const response = await axios.get<ViaCepResponse>(
        `https://viacep.com.br/ws/${cleanCep}/json/`
      );

      // Axios might have an interceptor that extracts .data already
      // Check if response is the data object itself or if we need response.data
      const data = (response as any).logradouro ? response as any : response.data;

      if (!data) {
        throw new Error('CEP não encontrado - resposta vazia');
      }

      // ViaCEP returns { erro: true } when CEP is not found
      if (data.erro === true) {
        throw new Error('CEP não encontrado');
      }

      return {
        cep: data.cep || cleanCep,
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || '',
      };
    } catch (error: any) {
      console.error('CEP Service Error:', error);

      // If it's already our custom error, re-throw it
      if (error.message && error.message.startsWith('CEP não encontrado')) {
        throw error;
      }

      if (error.response?.status === 404 || error.response?.data?.erro) {
        throw new Error('CEP não encontrado');
      }

      if (error.message && error.message !== 'Network Error') {
        throw error;
      }

      throw new Error('Erro ao buscar CEP. Verifique sua conexão.');
    }
  },
};

export default cepService;
